import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Command } from 'lucide-react';

/**
 * CommandPalette — Cmd+K (macOS) / Ctrl+K global fuzzy navigator.
 *
 * Merges:
 *   - top-level routes (/, /strategies, /portfolio, etc.)
 *   - admin tabs (EXECUTION/RISK/OPS/COMPLIANCE/USERS)
 *   - actions (download audit PDF, resolve stale violations, etc.)
 *
 * Commands are defined statically — no API round-trip. Fuzzy matcher is
 * a simple subsequence-with-score (no dependency bloat).
 */

const COMMANDS = [
  // Routes
  { id: 'nav-dashboard',   kind: 'route', label: 'Dashboard',         hint: '/',              go: '/' },
  { id: 'nav-strategies',  kind: 'route', label: 'Strategies',        hint: '/strategies',    go: '/strategies' },
  { id: 'nav-portfolio',   kind: 'route', label: 'Portfolio',         hint: '/portfolio',     go: '/portfolio' },
  { id: 'nav-venues',      kind: 'route', label: 'Venues',            hint: '/venues',        go: '/venues' },
  { id: 'nav-marketplace', kind: 'route', label: 'Marketplace',       hint: '/marketplace',   go: '/marketplace' },
  { id: 'nav-compliance',  kind: 'route', label: 'Compliance',        hint: '/compliance',    go: '/compliance' },
  { id: 'nav-settings',    kind: 'route', label: 'Settings',          hint: '/settings',      go: '/settings' },
  { id: 'nav-admin',       kind: 'route', label: 'Admin console',     hint: '/admin',         go: '/admin' },
  { id: 'nav-trust',       kind: 'route', label: 'Trust page',        hint: '/trust',         go: '/trust' },
  // Admin tabs
  { id: 'tab-execution',   kind: 'tab',   label: 'Admin · EXECUTION', hint: 'admin tab',      go: '/admin',      tab: 'EXECUTION' },
  { id: 'tab-risk',        kind: 'tab',   label: 'Admin · RISK',      hint: 'admin tab',      go: '/admin',      tab: 'RISK' },
  { id: 'tab-ops',         kind: 'tab',   label: 'Admin · OPS',       hint: 'admin tab',      go: '/admin',      tab: 'OPS' },
  { id: 'tab-compliance',  kind: 'tab',   label: 'Admin · COMPLIANCE',hint: 'admin tab',      go: '/admin',      tab: 'COMPLIANCE' },
  { id: 'tab-users',       kind: 'tab',   label: 'Admin · USERS',     hint: 'admin tab',      go: '/admin',      tab: 'USERS' },
  // Actions
  { id: 'act-audit-pdf',   kind: 'action',label: 'Download full 10-phase audit PDF',
    hint: 'opens RISK tab + triggers download', go: '/admin', tab: 'RISK',
    scroll: '[data-testid=download-system-audit-pdf]', click: true },
  { id: 'act-kill-switch', kind: 'action', label: 'Kill-switch · trading',
    hint: 'opens RISK tab', go: '/admin', tab: 'RISK' },
  { id: 'act-health',      kind: 'action', label: 'System health strip',
    hint: 'scroll to top', go: '/admin', scroll: '[data-testid=system-health-strip]' },
  { id: 'act-deposit',     kind: 'action', label: 'Deposit via Stripe',
    hint: 'EXECUTION tab', go: '/admin', tab: 'EXECUTION',
    scroll: '[data-testid=banking-deposit-panel]' },
];

function fuzzyScore(text, q) {
  if (!q) return 1;
  const t = text.toLowerCase();
  const query = q.toLowerCase();
  let ti = 0, qi = 0, score = 0, streak = 0;
  while (ti < t.length && qi < query.length) {
    if (t[ti] === query[qi]) {
      score += 1 + streak;
      streak += 1;
      qi += 1;
    } else {
      streak = 0;
    }
    ti += 1;
  }
  return qi === query.length ? score / t.length : 0;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard trigger (Cmd+K / Ctrl+K)
  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      setQ('');
      setIdx(0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    return COMMANDS
      .map((c) => ({ ...c, _s: fuzzyScore(c.label + ' ' + c.hint, q) }))
      .filter((c) => c._s > 0)
      .sort((a, b) => b._s - a._s)
      .slice(0, 10);
  }, [q]);

  const execute = useCallback((cmd) => {
    setOpen(false);
    if (cmd.tab) localStorage.setItem('apexflow.adminTab', cmd.tab);
    navigate(cmd.go);
    if (cmd.scroll || cmd.click) {
      setTimeout(() => {
        if (cmd.scroll) {
          const el = document.querySelector(cmd.scroll);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (cmd.click) {
          setTimeout(() => {
            const btn = document.querySelector(cmd.scroll);
            if (btn && typeof btn.click === 'function') btn.click();
          }, 600);
        }
      }, 300);
    }
  }, [navigate]);

  const onKeyInput = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[idx]) {
      e.preventDefault();
      execute(filtered[idx]);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="command-palette"
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '12vh',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 92vw)',
          background: '#0a0a0a',
          border: '1px solid var(--accent-amber)',
          borderRadius: 8,
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          overflow: 'hidden',
        }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          <Search size={16} style={{ color: 'var(--accent-amber)' }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={onKeyInput}
            placeholder="Type a command or route…"
            data-testid="command-palette-input"
            style={{
              flex: 1, background: 'transparent', border: 'none',
              outline: 'none', color: 'var(--text-primary)',
              fontSize: 14, fontFamily: 'var(--font-mono)',
            }}
          />
          <kbd style={{
            fontSize: 10, padding: '2px 6px',
            border: '1px solid var(--border)', borderRadius: 4,
            color: 'var(--text-muted)',
          }}>ESC</kbd>
        </div>
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '24px 16px', color: 'var(--text-muted)',
                           fontSize: 12 }}>
              No commands match. Try &ldquo;admin&rdquo;, &ldquo;audit&rdquo;, &ldquo;deposit&rdquo;.
            </div>
          )}
          {filtered.map((c, i) => (
            <button
              key={c.id}
              onClick={() => execute(c)}
              onMouseEnter={() => setIdx(i)}
              data-testid={`command-palette-item-${c.id}`}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer',
                background: i === idx ? '#1a1507' : 'transparent',
                border: 'none', borderLeft: i === idx
                  ? '3px solid var(--accent-amber)' : '3px solid transparent',
                padding: '10px 16px',
                display: 'flex', alignItems: 'center', gap: 10,
                color: 'var(--text-primary)',
                fontSize: 12,
              }}>
              <span style={{
                fontSize: 9, letterSpacing: '0.1em',
                color: 'var(--accent-amber)',
                minWidth: 52,
              }}>
                {c.kind.toUpperCase()}
              </span>
              <span style={{ flex: 1 }}>{c.label}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 10,
                              fontFamily: 'var(--font-mono)' }}>{c.hint}</span>
              <ArrowRight size={12} style={{ opacity: i === idx ? 1 : 0.3,
                                              color: 'var(--accent-amber)' }} />
            </button>
          ))}
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          padding: '8px 16px', borderTop: '1px solid var(--border)',
          color: 'var(--text-muted)', fontSize: 10,
        }}>
          <span>
            <Command size={10} style={{ display: 'inline', marginRight: 4 }} />
            K to toggle · ↑↓ to navigate · ↵ to open
          </span>
          <span>ApexFlow · {filtered.length} results</span>
        </div>
      </div>
    </div>
  );
}
